import React, { useState, useEffect } from "react";
import {
  Card,
  DatePicker,
  Button,
  Table,
  Spin,
  message,
  Tag,
  Image,
  Tooltip,
  Calendar,
  Radio,
} from "antd";
import { CalendarOutlined, TableOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";

// Simple calendar cell renderer
const getEventsForDate = (date, schedule) => {
  const dateStr = dayjs(date).format("YYYY-MM-DD");
  return schedule.filter(
    (item) => dayjs(item.DateVal).format("YYYY-MM-DD") === dateStr
  );
};

// Helper: get all days in the week of a given date (Sunday to Saturday)
const getWeekDays = (date) => {
  const startOfWeek = dayjs(date).startOf("week");
  return Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, "day"));
};

const InstaPostSchedule = () => {
  // Set default dates: first and last day of current month
  const firstDay = dayjs().startOf("month");
  const lastDay = dayjs().endOf("month");

  const [fromDate, setFromDate] = useState(firstDay);
  const [toDate, setToDate] = useState(lastDay);
  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [msgApi, contextHolder] = message.useMessage();
  const [viewMode, setViewMode] = useState("table"); // "table" or "calendar"
  const [calendarMonth, setCalendarMonth] = useState(firstDay);
  const [calendarViewType, setCalendarViewType] = useState("month"); // "month" | "week" | "day"
  const [selectedDay, setSelectedDay] = useState(dayjs());

  const fetchSchedule = async (from = fromDate, to = toDate) => {
    if (!from || !to) {
      msgApi.error("Please select both From and To dates.");
      return;
    }
    setLoading(true);
    try {
      const fromStr = dayjs(from).format("DD-MM-YYYY");
      const toStr = dayjs(to).format("DD-MM-YYYY");
      const tokenString = localStorage.getItem("token");
      const response = await axios.get(
        `${global.config.APIURL.BaseURL}api/InstaPostSchedule/GetScheduleByDates?From=${fromStr}&To=${toStr}`,
        {
          headers: {
            Authorization: `Bearer ${tokenString}`,
          },
        }
      );
      setSchedule(response.data || []);
    } catch (err) {
      msgApi.error("Failed to fetch schedule.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedule();
    // eslint-disable-next-line
  }, []);

  const statusColorMap = {
    Done: "green",
    Pending: "orange",
    Error: "red",
  };

  const columns = [
    { title: "ID", dataIndex: "ID", key: "ID" },
    {
      title: "Date & Time",
      dataIndex: "DateVal",
      key: "DateVal",
      render: (val) => dayjs(val).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: "Image",
      dataIndex: "ImageURL",
      key: "ImageURL",
      render: (url) =>
        url ? (
          <Image
            src={url}
            alt="Product"
            width={60}
            height={60}
            style={{ objectFit: "cover", borderRadius: 6 }}
            preview={false}
          />
        ) : (
          "-"
        ),
    },
    { title: "Product ID", dataIndex: "ProductID", key: "ProductID" },
    {
      title: "Post Type",
      dataIndex: "PostType",
      key: "PostType",
      render: (type) =>
        type === "FANCY" ? (
          <Tag color="blue">FANCY</Tag>
        ) : (
          <Tag color="magenta">RAJPUTI</Tag>
        ),
    },
    {
      title: "POSTON",
      dataIndex: "POSTON",
      key: "POSTON",
      render: (type) =>
        type === "FANCY" ? (
          <Tag color="blue">FANCY</Tag>
        ) : (
          <Tag color="magenta">RAJPUTI</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      render: (status) => (
        <Tag color={statusColorMap[status] || "default"}>{status}</Tag>
      ),
    },
  ];

  // Calendar cell rendering (for month and week)
  const renderCalendarCell = (date) => {
    const events = getEventsForDate(date, schedule).sort(
      (a, b) => new Date(a.DateVal) - new Date(b.DateVal)
    );
    if (events.length === 0) return null;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {events.map((item) => (
          <Tooltip
            key={item.ID}
            placement="top"
            title={
              <div>
                <div>
                  <b>{dayjs(item.DateVal).format("DD-MM-YYYY HH:mm")}</b>
                </div>
                <div>
                  <b>Product ID:</b> {item.ProductID}
                </div>
                <div>
                  <b>Post Type:</b>{" "}
                  <Tag color={item.PostType === "FANCY" ? "blue" : "magenta"}>
                    {item.PostType}
                  </Tag>
                </div>
                <div>
                  <b>POSTON:</b>{" "}
                  <Tag color={item.POSTON === "FANCY" ? "blue" : "magenta"}>
                    {item.POSTON}
                  </Tag>
                </div>
                <div>
                  <b>Status:</b>{" "}
                  <Tag color={statusColorMap[item.Status] || "default"}>
                    {item.Status}
                  </Tag>
                </div>
                <div>
                  <Image
                    src={item.ImageURL}
                    alt="Product"
                    width={80}
                    height={80}
                    style={{
                      objectFit: "cover",
                      borderRadius: 6,
                      marginTop: 4,
                    }}
                    preview={false}
                  />
                </div>
              </div>
            }
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 4,
                overflow: "hidden",
                border: `2px solid ${statusColorMap[item.Status] || "#eee"}`,
                background: "#fafafa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 2,
                cursor: "pointer",
              }}
            >
              <img
                src={item.ImageURL}
                alt="Product"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </div>
          </Tooltip>
        ))}
      </div>
    );
  };

  // Render for single day view
  const renderSingleDayView = () => {
    const events = getEventsForDate(selectedDay, schedule).sort(
      (a, b) => new Date(a.DateVal) - new Date(b.DateVal)
    );
    return (
      <div>
        <h3>Events for {selectedDay.format("DD-MM-YYYY")}</h3>
        {events.length === 0 ? (
          <div>No events scheduled for this day.</div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {events.map((item) => (
              <Tooltip
                key={item.ID}
                placement="top"
                title={
                  <div>
                    <div>
                      <b>{dayjs(item.DateVal).format("DD-MM-YYYY HH:mm")}</b>
                    </div>
                    <div>
                      <b>Product ID:</b> {item.ProductID}
                    </div>
                    <div>
                      <b>Post Type:</b>{" "}
                      <Tag
                        color={item.PostType === "FANCY" ? "blue" : "magenta"}
                      >
                        {item.PostType}
                      </Tag>
                    </div>
                    <div>
                      <b>POSTON:</b>{" "}
                      <Tag color={item.POSTON === "FANCY" ? "blue" : "magenta"}>
                        {item.POSTON}
                      </Tag>
                    </div>
                    <div>
                      <b>Status:</b>{" "}
                      <Tag color={statusColorMap[item.Status] || "default"}>
                        {item.Status}
                      </Tag>
                    </div>
                    <div>
                      <Image
                        src={item.ImageURL}
                        alt="Product"
                        width={80}
                        height={80}
                        style={{
                          objectFit: "cover",
                          borderRadius: 6,
                          marginTop: 4,
                        }}
                        preview={false}
                      />
                    </div>
                  </div>
                }
              >
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 6,
                    overflow: "hidden",
                    border: `2px solid ${
                      statusColorMap[item.Status] || "#eee"
                    }`,
                    background: "#fafafa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={item.ImageURL}
                    alt="Product"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render for week view
  const renderWeekView = () => {
    const weekDays = getWeekDays(calendarMonth);
    return (
      <div>
        <h3>
          Events for week of {weekDays[0].format("DD-MM-YYYY")} -{" "}
          {weekDays[6].format("DD-MM-YYYY")}
        </h3>
        <div style={{ display: "flex", gap: 16 }}>
          {weekDays.map((day) => {
            const events = getEventsForDate(day, schedule).sort(
              (a, b) => new Date(a.DateVal) - new Date(b.DateVal)
            );
            return (
              <div key={day.format("YYYY-MM-DD")} style={{ minWidth: 120 }}>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: 4,
                    textAlign: "center",
                  }}
                >
                  {day.format("ddd, DD")}
                </div>
                {events.length === 0 ? (
                  <div style={{ color: "#bbb", textAlign: "center" }}>
                    No events
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      justifyContent: "center",
                    }}
                  >
                    {events.map((item) => (
                      <Tooltip
                        key={item.ID}
                        placement="top"
                        title={
                          <div>
                            <div>
                              <b>
                                {dayjs(item.DateVal).format("DD-MM-YYYY HH:mm")}
                              </b>
                            </div>
                            <div>
                              <b>Product ID:</b> {item.ProductID}
                            </div>
                            <div>
                              <b>Post Type:</b>{" "}
                              <Tag
                                color={
                                  item.PostType === "FANCY" ? "blue" : "magenta"
                                }
                              >
                                {item.PostType}
                              </Tag>
                            </div>
                            <div>
                              <b>POSTON:</b>{" "}
                              <Tag
                                color={
                                  item.POSTON === "FANCY" ? "blue" : "magenta"
                                }
                              >
                                {item.POSTON}
                              </Tag>
                            </div>
                            <div>
                              <b>Status:</b>{" "}
                              <Tag
                                color={statusColorMap[item.Status] || "default"}
                              >
                                {item.Status}
                              </Tag>
                            </div>
                            <div>
                              <Image
                                src={item.ImageURL}
                                alt="Product"
                                width={80}
                                height={80}
                                style={{
                                  objectFit: "cover",
                                  borderRadius: 6,
                                  marginTop: 4,
                                }}
                                preview={false}
                              />
                            </div>
                          </div>
                        }
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 4,
                            overflow: "hidden",
                            border: `2px solid ${
                              statusColorMap[item.Status] || "#eee"
                            }`,
                            background: "#fafafa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 2,
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={item.ImageURL}
                            alt="Product"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // When switching to calendar view, set calendarMonth to fromDate's month
  useEffect(() => {
    if (viewMode === "calendar") {
      setCalendarMonth(fromDate.startOf("month"));
    }
    // eslint-disable-next-line
  }, [viewMode]);

  return (
    <Card
      title="Instagram Post Schedule"
      style={{ maxWidth: 1000, margin: "32px auto" }}
      extra={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tooltip title="Table View">
            <Button
              icon={<TableOutlined />}
              type={viewMode === "table" ? "primary" : "default"}
              onClick={() => setViewMode("table")}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Calendar View">
            <Button
              icon={<CalendarOutlined />}
              type={viewMode === "calendar" ? "primary" : "default"}
              onClick={() => setViewMode("calendar")}
              size="small"
            />
          </Tooltip>
        </div>
      }
    >
      {contextHolder}
      <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
        <DatePicker
          placeholder="From Date"
          value={fromDate}
          format="DD-MM-YYYY"
          onChange={setFromDate}
          style={{ width: 160 }}
        />
        <DatePicker
          placeholder="To Date"
          value={toDate}
          format="DD-MM-YYYY"
          onChange={setToDate}
          style={{ width: 160 }}
        />
        <Button type="primary" onClick={() => fetchSchedule()}>
          Get Schedule
        </Button>
      </div>
      {viewMode === "calendar" && (
        <div style={{ marginBottom: 16, textAlign: "right" }}>
          <Radio.Group
            value={calendarViewType}
            onChange={(e) => setCalendarViewType(e.target.value)}
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="month">Month</Radio.Button>
            <Radio.Button value="week">Week</Radio.Button>
            <Radio.Button value="day">Day</Radio.Button>
          </Radio.Group>
        </div>
      )}
      <Spin spinning={loading}>
        {viewMode === "table" ? (
          <Table
            dataSource={schedule}
            columns={columns}
            rowKey="ID"
            pagination={{ pageSize: 15 }}
          />
        ) : calendarViewType === "day" ? (
          <div>
            <DatePicker
              value={selectedDay}
              format="DD-MM-YYYY"
              onChange={setSelectedDay}
              style={{ marginBottom: 16 }}
            />
            {renderSingleDayView()}
          </div>
        ) : calendarViewType === "week" ? (
          <div>
            <DatePicker
              picker="week"
              value={calendarMonth}
              onChange={(date) => setCalendarMonth(date.startOf("week"))}
              style={{ marginBottom: 16 }}
            />
            {renderWeekView()}
          </div>
        ) : (
          <Calendar
            value={calendarMonth}
            onPanelChange={(date) => {
              setCalendarMonth(date.startOf("month"));
              const monthStart = date.startOf("month");
              const monthEnd = date.endOf("month");
              setFromDate(monthStart);
              setToDate(monthEnd);
              fetchSchedule(monthStart, monthEnd);
            }}
            dateCellRender={renderCalendarCell}
            mode="month"
            fullscreen
          />
        )}
      </Spin>
    </Card>
  );
};

export default InstaPostSchedule;
